import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Clock, ArrowRight, Loader2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Blog() {
  const [articles, setArticles] = useState(null);
  useEffect(() => { api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([])); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="blog-page">
      <div className="max-w-2xl mb-12">
        <span className="overline text-accent">Guidance</span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2 mb-4">Compliance guidance for CQC &amp; Ofsted managers</h1>
        <p className="text-muted-foreground text-base md:text-lg">Practical articles from Futuregen Innovations on inspection readiness, leadership, safeguarding and registering a new service.</p>
      </div>

      {!articles ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : articles.length === 0 ? (
        <p className="text-muted-foreground" data-testid="blog-empty">No articles published yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="blog-list">
          {articles.map((a, i) => (
            <Link key={a.id} to={`/guidance/${a.slug}`} data-testid={`article-card-${a.slug}`}
              className={`group bg-card border border-border rounded-2xl p-7 shadow-sm hover:border-primary/40 hover:shadow-md transition-all flex flex-col ${i === 0 ? "md:col-span-2 lg:flex-row lg:items-end lg:gap-10" : ""}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="secondary">{a.category}</Badge>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.reading_minutes} min read</span>
                </div>
                <h2 className={`font-display font-bold tracking-tight mb-3 group-hover:text-primary transition-colors ${i === 0 ? "text-2xl sm:text-3xl" : "text-xl"}`}>{a.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.excerpt}</p>
              </div>
              <div className="flex items-center justify-between mt-6 text-sm shrink-0 lg:w-56">
                <span className="text-muted-foreground">{fmt(a.published_at)}</span>
                <span className="inline-flex items-center gap-1 font-semibold text-primary">Read <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-12"><NewsletterSignup source="guidance" /></div>
      <div className="mt-8 rounded-2xl border border-border bg-secondary/50 p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6" /></div>
        <div className="flex-1">
          <p className="font-display font-bold text-lg">Have a question these articles don't answer?</p>
          <p className="text-sm text-muted-foreground">Ask the AI Compliance Assistant — and if it can't help, a specialist will.</p>
        </div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline" data-testid="blog-assistant-cta">Ask the assistant <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
